import {
  IDataProviderFilter,
  IFilter,
  IFilterOperator,
  Logic,
  Operator,
} from 'core-components/types'

export interface IDataProviderFilterExtended<T> extends IDataProviderFilter {
  availableData: T[]
  resultData: T[]
}

function isNestedFilter(filter: IFilter | IDataProviderFilter) {
  return filter && filter.hasOwnProperty && filter.hasOwnProperty('logic')
}

export class StaticFilterService {
  public static filterData = <T>(
    filters: (IFilter | IDataProviderFilterExtended<T> | IDataProviderFilter)[],
    data: T[],
    parentFilter: IDataProviderFilterExtended<T>
  ) => {
    filters.forEach((filter) => {
      if (StaticFilterService.isDataProviderFilter(filter)) {
        const newParentFilter: IDataProviderFilterExtended<T> = {
          ...(filter as IDataProviderFilter),
          resultData: [] as T[],
          availableData: [...parentFilter.availableData],
        }
        StaticFilterService.filterData(
          (filter as IDataProviderFilter).filters,
          data,
          newParentFilter
        )
        this.mergeFilterResult(newParentFilter.resultData, parentFilter)
      } else {
        StaticFilterService.filterWithoutLogic(filter as IFilter, data, parentFilter)
      }
    })
  }

  public static isDataProviderFilter = <T>(
    filter: IDataProviderFilter | IDataProviderFilterExtended<T> | IFilter
  ) => (filter as IDataProviderFilter).logic

  public static matchFilter = <T>(value: T, filters: IDataProviderFilter): boolean => {
    const values = filters.filters.map((el) => {
      if (isNestedFilter(el)) {
        const filter = el as IDataProviderFilter
        return StaticFilterService.matchFilter(value, filter)
      } else {
        const filter = el as IFilter
        const field = value[filter.field]
        return StaticFilterService.filterCheck(
          field,
          filter.operator,
          filter.value as string | string[]
        )
      }
    })
    if (values.length === 0) {
      return true
    }
    if (filters.logic === Logic.AND) {
      return values.reduce((res, el) => {
        return res && el
      }, true)
    } else {
      return values.reduce((res, el) => {
        return res || el
      }, false)
    }
  }

  private static filterCheck = (
    field: string | undefined,
    operator: Operator | IFilterOperator,
    value: string | string[]
  ): boolean => {
    if (operator === Operator.EQUAL) {
      if (typeof field === 'string' && typeof value === 'string') {
        return field.toLowerCase() === `${value}`.toLowerCase()
      }
      return `${field}` === `${value}`
    }
    if (operator === Operator.CONTAINS) {
      // tslint:disable-next-line:no-bitwise
      return !!~`${field}`.toLowerCase().indexOf(`${value}`.toLowerCase())
    }
    if (operator === Operator.DOESNOTCONTAIN) {
      return `${field}`.indexOf(`${value}`) === -1
    }
    if (operator === Operator.NOTEQUAL) {
      return field !== value
    }
    if (operator === Operator.ISNULL) {
      return field === null
    }
    if (operator === Operator.ISNOTNULL) {
      return field !== null
    }
    if (operator === Operator.LT) {
      return field ? field < value : false
    }
    if (operator === Operator.LTE) {
      return field ? field <= value : false
    }
    if (operator === Operator.GT) {
      return field ? field > value : false
    }
    if (operator === Operator.GTE) {
      return field ? field >= value : false
    }
    if (operator === Operator.STARTSWITH && typeof value === 'string') {
      return field?.startsWith(value) || false
    }
    if (operator === Operator.ENDSWITH && typeof value === 'string') {
      return field?.endsWith(value) || false
    }
    if (operator === Operator.ISEMPTY) {
      return field === null || field === undefined || field.length === 0
    }
    if (operator === Operator.ISNOTEMPTY) {
      return field !== null && field !== undefined && field.length !== 0
    }
    if (operator === Operator.IN) {
      if (Array.isArray(value)) {
        return value.some((el) => `${el}` === `${field}`)
      }
      let arr
      try {
        arr = JSON.parse(value)
      } catch (e) {}
      if (arr) {
        return arr.some((el: string) => `${el}` === `${field}`)
      }
    }
    if (operator === Operator.INCLUDE) {
      if (Array.isArray(value)) {
        return value.some((el) => field && field.includes(el))
      }
      let arr
      try {
        arr = JSON.parse(value)
      } catch (e) {}
      if (arr) {
        return arr.some((el: string) => field && field.includes(el))
      }
    }

    return false
  }

  private static filterWithoutLogic = <T>(
    filter: IFilter,
    data: T[],
    parentFilter: IDataProviderFilterExtended<T>
  ) => {
    const filteredData = parentFilter.availableData.filter((el) => {
      return StaticFilterService.filterCheck(
        el[filter.field],
        filter.operator,
        filter.value as string | string[]
      )
    })
    this.mergeFilterResult(filteredData, parentFilter)
  }

  private static mergeFilterResult = <T>(
    data: T[],
    parentFilter: IDataProviderFilterExtended<T>
  ) => {
    switch (parentFilter.logic) {
      case Logic.OR:
        StaticFilterService.assignByOrFilter(data, parentFilter)
        return
      case Logic.AND:
      default:
        StaticFilterService.assignByAndFilter(data, parentFilter)
    }
  }

  private static assignByOrFilter = <T>(
    filteredData: T[],
    parentFilter: IDataProviderFilterExtended<T>
  ) => {
    filteredData.forEach((filteredItem) => {
      if (!parentFilter.resultData.find((resultItem) => resultItem === filteredItem)) {
        parentFilter.resultData.push(filteredItem)
      }
    })
  }

  private static assignByAndFilter = <T>(
    filteredData: T[],
    parentFilter: IDataProviderFilterExtended<T>
  ) => {
    parentFilter.availableData = [...filteredData]
    parentFilter.resultData = [...filteredData]
  }
}
